import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const EMPTY = { name: "", process_limit: "", user_limit: "", message_limit: "", ai_quota: "",
                features: "", price_monthly: 0, price_yearly: 0, overage_pct: 10, status: "ativo" };
const DEP_LABELS = { escritorios: "escritório(s) vinculado(s)", assinaturas: "assinatura(s)", tarifas_whatsapp: "tarifa(s) WhatsApp" };

export default function AdminPlanos() {
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [delPlan, setDelPlan] = useState(null);
  const [delDeps, setDelDeps] = useState(null);
  const [delLoading, setDelLoading] = useState(false);

  const load = () => api.get("/admin/plans").then((r) => setPlans(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...EMPTY, ...p, process_limit: p.process_limit ?? "", user_limit: p.user_limit ?? "",
              message_limit: p.message_limit ?? "", ai_quota: p.ai_quota ?? "" });
    setOpen(true);
  };

  const save = async () => {
    const payload = { ...form,
      process_limit: form.process_limit === "" ? null : Number(form.process_limit),
      user_limit: form.user_limit === "" ? null : Number(form.user_limit),
      message_limit: form.message_limit === "" ? null : Number(form.message_limit),
      ai_quota: form.ai_quota === "" ? null : Number(form.ai_quota),
      price_monthly: Number(form.price_monthly) || 0, price_yearly: Number(form.price_yearly) || 0,
      overage_pct: Number(form.overage_pct) || 0 };
    try {
      if (editing) await api.patch(`/admin/plans/${editing.id}`, payload);
      else await api.post("/admin/plans", payload);
      toast.success(editing ? "Plano atualizado" : "Plano criado");
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const confirmDelete = async () => {
    setDelLoading(true);
    try {
      await api.delete(`/admin/plans/${delPlan.id}`);
      toast.success("Plano excluído definitivamente");
      setDelPlan(null); setDelDeps(null);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.dependencies) {
        setDelDeps(d);
      } else {
        toast.error(apiError(e));
        setDelPlan(null); setDelDeps(null);
      }
    } finally { setDelLoading(false); }
  };

  const deactivate = async () => {
    setDelLoading(true);
    try {
      await api.post(`/admin/plans/${delPlan.id}/deactivate`);
      toast.success("Plano desativado — o histórico foi preservado");
      setDelPlan(null); setDelDeps(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setDelLoading(false); }
  };

  const numField = (key, label, tid) => (
    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-xs">{label}</Label>
      <Input type="number" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        data-testid={tid} placeholder="vazio = ilimitado" className="bg-[#090A0F] border-[#23283E]" />
    </div>
  );

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="admin-planos-page">
      <div className="flex items-center justify-between fade-up">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-100">Planos</h1>
          <p className="text-sm text-zinc-500 mt-1">Modelo baseado em quantidade de processos. Preços configuráveis.</p>
        </div>
        <Button onClick={openNew} data-testid="add-plan-btn" className="brand-gradient brand-gradient-hover text-white border-0">
          <Plus size={16} className="mr-1.5" /> Novo plano
        </Button>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4 fade-up" data-testid="plans-grid">
        {plans.map((p) => (
          <div key={p.id} data-testid={`plan-card-${p.id}`}
            className="rounded-xl border border-[#23283E] bg-[#0F111A] p-5 hover:border-indigo-500/40 transition-colors duration-200">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-zinc-100">{p.name}</h3>
              <Badge className={p.status === "ativo"
                ? "bg-emerald-950/60 border border-emerald-700/50 text-emerald-400"
                : "bg-zinc-800 border border-zinc-700 text-zinc-400"}>{p.status}</Badge>
            </div>
            <p className="font-display text-2xl font-bold brand-gradient-text mt-3">
              {p.process_limit ? `até ${p.process_limit}` : "ilimitado"}
            </p>
            <p className="text-xs text-zinc-500">processos</p>
            <div className="text-xs text-zinc-500 mt-3 space-y-1">
              <p>Usuários: {p.user_limit ?? "ilimitado"} • Mensagens: {p.message_limit ?? "ilimitado"}</p>
              <p>Overage permitido: {p.overage_pct}%</p>
              <p>Mensal: R$ {p.price_monthly} • Anual: R$ {p.price_yearly}</p>
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`edit-plan-${p.id}`}
                className="border-[#3F476C] text-zinc-300 flex-1">
                <Pencil size={13} className="mr-1.5" /> Editar
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setDelPlan(p); setDelDeps(null); }}
                data-testid={`delete-plan-${p.id}`}
                className="border-rose-800/50 text-rose-400 hover:bg-rose-950/40">
                <Trash2 size={13} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F111A] border-[#23283E] max-w-lg" data-testid="plan-dialog">
          <DialogHeader><DialogTitle className="font-display text-zinc-100">{editing ? "Editar plano" : "Novo plano"}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value.toUpperCase() })}
                data-testid="plan-name-input" placeholder="STARTER" className="bg-[#090A0F] border-[#23283E]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {numField("process_limit", "Limite de processos", "plan-process-limit")}
              {numField("user_limit", "Limite de usuários", "plan-user-limit")}
              {numField("message_limit", "Limite de mensagens", "plan-message-limit")}
              {numField("ai_quota", "Franquia de IA", "plan-ai-quota")}
              {numField("price_monthly", "Preço mensal R$", "plan-price-monthly")}
              {numField("price_yearly", "Preço anual R$", "plan-price-yearly")}
              {numField("overage_pct", "Overage máximo %", "plan-overage")}
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Recursos incluídos</Label>
              <Input value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })}
                data-testid="plan-features-input" placeholder="WhatsApp, IA de atendimento, relatórios…"
                className="bg-[#090A0F] border-[#23283E]" />
            </div>
            <Button onClick={save} data-testid="plan-save-btn" className="w-full brand-gradient brand-gradient-hover text-white border-0">
              Salvar plano
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delPlan} onOpenChange={() => { setDelPlan(null); setDelDeps(null); }}>
        <DialogContent className="bg-[#0F111A] border-[#23283E]" data-testid="plan-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-zinc-100">Excluir plano</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">Esta operação fica registrada no log administrativo.</DialogDescription>
          </DialogHeader>
          {!delDeps ? (
            <div className="space-y-4">
              <p className="text-sm text-zinc-300">
                Tem certeza que deseja excluir este plano? <strong className="text-zinc-100">{delPlan?.name}</strong>
              </p>
              <p className="text-xs text-zinc-500">Se houver escritórios, assinaturas ou tarifas vinculadas, a exclusão será bloqueada para preservar o histórico.</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDelPlan(null)} data-testid="plan-delete-cancel"
                  className="border-[#3F476C] text-zinc-300 flex-1">Cancelar</Button>
                <Button onClick={confirmDelete} disabled={delLoading} data-testid="plan-delete-confirm"
                  className="bg-rose-600 hover:bg-rose-500 text-white border-0 flex-1">
                  {delLoading ? "Verificando…" : "Excluir definitivamente"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="plan-delete-blocked">
              <p className="text-sm text-amber-300">{delDeps.message}</p>
              <div className="rounded-lg border border-[#23283E] bg-[#090A0F] px-3.5 py-3 space-y-1">
                {Object.entries(delDeps.dependencies).map(([k, v]) => (
                  <p key={k} className="text-xs text-zinc-300" data-testid={`plan-dep-${k}`}>{v} {DEP_LABELS[k] || k}</p>
                ))}
              </div>
              <p className="text-xs text-zinc-500">Para preservar o histórico, desative o plano em vez de excluí-lo.</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setDelPlan(null); setDelDeps(null); }} data-testid="plan-delete-close"
                  className="border-[#3F476C] text-zinc-300 flex-1">Fechar</Button>
                <Button onClick={deactivate} disabled={delLoading} data-testid="plan-deactivate-btn"
                  className="brand-gradient brand-gradient-hover text-white border-0 flex-1">
                  <Ban size={13} className="mr-1.5" /> Desativar plano
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
